import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Search, Trash2 } from 'lucide-react';
import { getConnectorDisplayName } from '../../Connections/catalog';
import { listDataStores } from '../../Connections/dataStoreApi';
import { logDataModels } from '../dataModelsLogger';
import type { DataSource, DataSourceKind, ModelTable } from '../dataModelsTypes';
import { sourcesForSpace } from './SpaceOverview';
import { DialogShell, SourceIconTile, ghostBtn, primaryBtn, searchClass, surfaces } from './dataModelsUi';

export default function WorkspaceConnectionsPanel({
  isLight,
  sourceIds,
  sources,
  tables,
  busy,
  onAttach,
  onDetach,
}: {
  isLight: boolean;
  sourceIds: string[];
  sources: DataSource[];
  tables: ModelTable[];
  busy: boolean;
  onAttach: (sources: DataSource[]) => Promise<void>;
  onDetach: (sourceId: string) => Promise<void>;
}) {
  const ui = surfaces(isLight);
  const muted = ui.muted;
  const attached = sourcesForSpace(tables, sources, sourceIds);
  const [attachOpen, setAttachOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DataSource | null>(null);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className={`text-sm font-semibold ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>
          Connections
          <span className={`ml-2 text-[13px] font-normal ${muted}`}>{attached.length}</span>
        </h3>
        <button
          type="button"
          className={primaryBtn('!h-7 !px-2.5 gap-1 text-[12px]')}
          disabled={busy}
          onClick={() => {
            setAttachOpen(true);
            logDataModels('app', 'workspace_connections_attach_opened', { attached: attached.length });
          }}
        >
          <Plus size={12} strokeWidth={2.25} />
          Attach connection
        </button>
      </div>

      {attached.length ? (
        <div className={`overflow-hidden rounded-md border ${ui.card}`}>
          <table className="min-w-full text-[13px]">
            <thead className={ui.tableHead}>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5">Connection</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Assets</th>
                <th className="w-12 px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {attached.map((source) => {
                const assets = tables.filter((table) => table.sourceId === source.id).length;
                const canDelete = assets === 0;
                return (
                  <tr key={source.id} className={`border-t ${ui.border}`}>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center gap-2">
                        <SourceIconTile kind={source.kind} isLight={isLight} box="h-7 w-7" />
                        <span className="min-w-0">
                          <span className={`block truncate font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{source.name}</span>
                          {source.environment ? <span className={`block truncate text-[11px] ${muted}`}>{source.environment}</span> : null}
                        </span>
                      </span>
                    </td>
                    <td className={`px-3 py-2.5 ${muted}`}>{source.connectionType || source.kind}</td>
                    <td className="px-3 py-2.5 tabular-nums">{assets}</td>
                    <td className="px-3 py-2.5">
                      {canDelete ? (
                        <button
                          type="button"
                          className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${
                            isLight ? 'text-[#5C6570] hover:bg-[#F2F4F7]' : 'text-slate-300 hover:bg-white/10'
                          }`}
                          title="Remove connection"
                          aria-label={`Remove ${source.name}`}
                          disabled={busy}
                          onClick={() => setPendingDelete(source)}
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : (
                        <span className={`block text-[11px] ${muted}`} title="In use by a catalog">In use</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={`py-8 text-sm ${muted}`}>No connections on this WorkSpace yet.</p>
      )}

      {attachOpen ? (
        <AttachConnectionsDialog
          isLight={isLight}
          attachedIds={attached.map((source) => source.id)}
          busy={busy}
          onClose={() => setAttachOpen(false)}
          onApply={async (next) => {
            await onAttach(next);
            setAttachOpen(false);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <DialogShell
          title="Remove connection"
          subtitle={`${pendingDelete.name} will be detached from this WorkSpace. The connection itself is not deleted.`}
          isLight={isLight}
          onClose={() => setPendingDelete(null)}
          footer={
            <>
              <button type="button" className={ghostBtn(isLight)} onClick={() => setPendingDelete(null)}>Cancel</button>
              <button
                type="button"
                className={primaryBtn()}
                disabled={busy}
                onClick={() => {
                  const sourceId = pendingDelete.id;
                  void onDetach(sourceId).then(() => setPendingDelete(null));
                }}
              >
                Remove
              </button>
            </>
          }
        >
          <p className={`text-sm ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>
            This connection has no assets in any catalog on this WorkSpace.
          </p>
        </DialogShell>
      ) : null}
    </div>
  );
}

function AttachConnectionsDialog({
  isLight,
  attachedIds,
  busy,
  onClose,
  onApply,
}: {
  isLight: boolean;
  attachedIds: string[];
  busy: boolean;
  onClose: () => void;
  onApply: (sources: DataSource[]) => Promise<void>;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const [rows, setRows] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const attached = useMemo(() => new Set(attachedIds), [attachedIds]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    listDataStores()
      .then((list) => {
        if (cancelled) return;
        setRows(list.filter((row) => !attached.has(row.id)).map(toConnectionSource));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Unable to load connections');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attached]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((source) =>
      `${source.name} ${source.connectionType || ''} ${source.environment}`.toLowerCase().includes(needle)
    );
  }, [query, rows]);

  return (
    <DialogShell
      title="Attach connections"
      subtitle="Choose connections that are not already on this WorkSpace."
      isLight={isLight}
      onClose={onClose}
      size="medium"
      footer={
        <>
          <span className={`mr-auto self-center text-[12px] ${muted}`}>{selected.length} selected</span>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={busy || !selected.length}
            onClick={() => onApply(rows.filter((source) => selected.includes(source.id)))}
          >
            Attach
          </button>
        </>
      }
    >
      <label className="relative mb-3 block">
        <Search size={13} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter connections"
          className={searchClass(isLight)}
        />
      </label>
      {loading ? <p className={`py-6 text-sm ${muted}`}>Loading connections…</p> : null}
      {error ? <p className="py-6 text-sm text-rose-600">{error}</p> : null}
      <div className="max-h-[18rem] space-y-0.5 overflow-y-auto">
        {visible.map((source) => {
          const checked = selected.includes(source.id);
          return (
            <button
              key={source.id}
              type="button"
              onClick={() => setSelected((current) => (checked ? current.filter((id) => id !== source.id) : [...current, source.id]))}
              className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left ${isLight ? 'hover:bg-[#F3F7FB]' : 'hover:bg-white/5'}`}
            >
              <span className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                checked ? 'border-[#1B6CA8] bg-[#1B6CA8]' : isLight ? 'border-[#D8DCE2]' : 'border-white/20'
              }`}
              >
                {checked ? <Check size={11} className="text-white" /> : null}
              </span>
              <SourceIconTile kind={source.kind} isLight={isLight} box="h-6 w-6" />
              <span className="min-w-0">
                <span className={`block truncate text-[13px] ${ink}`}>{source.name}</span>
                <span className={`block truncate text-[11px] ${muted}`}>
                  {source.connectionType || source.kind}
                  {source.environment ? ` · ${source.environment}` : ''}
                </span>
              </span>
            </button>
          );
        })}
        {!loading && !error && !visible.length ? (
          <p className={`px-2 py-6 text-sm ${muted}`}>{rows.length ? 'No connections match.' : 'All connections are already attached.'}</p>
        ) : null}
      </div>
    </DialogShell>
  );
}

function toConnectionSource(row: { id: string; name: string; connectorKey: string; environment?: string }): DataSource {
  return {
    id: row.id,
    name: row.name,
    kind: kindForConnector(row.connectorKey),
    environment: row.environment || '',
    connectionType: getConnectorDisplayName(row.connectorKey),
    connectorKey: row.connectorKey,
  };
}

function kindForConnector(connectorKey: string): DataSourceKind {
  const key = connectorKey.trim().toLowerCase();
  if (key === 'snowflake') return 'snowflake';
  if (key === 'mongodb' || key === 'firestore') return 'mongodb';
  if (key === 'bigquery') return 'bigquery';
  if (key === 'mssql' || key === 'sqlserver') return 'sqlserver';
  if (key === 'databricks') return 'databricks';
  return 'postgresql';
}
